import React, { useState, useEffect } from "react";
import {
  Grid,
  Select,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Box,
  Chip,
  IconButton,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useCompanies } from "../../hooks/useCompanies";
import { useChannels } from "../../hooks/useChannels";
import { useUsers } from "../../hooks/useUsers";
import { useAgents } from "../../hooks/useAgents";
import { useRequests } from "../../hooks/useRequests";
import { useTypes } from "../../hooks/useTypes";
import StatusSelector from "./StatusSelector";

/** Valor para input datetime-local a partir do retorno da API (YYYY-MM-DD HH:mm:ss). */
function nextContactToInputValue(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (!s) return "";
  return s.replace(" ", "T").slice(0, 16);
}

const RequestDetails = ({
  selectedConversation,
  onSelectedConversationUpdate,
  onRefetchConversationDetails,
}) => {
  /* =================== State principal normalizado =================== */
  const normalizeSelectValue = (value) => {
    if (value === null || value === undefined) return "";
    if (value === 0 || value === "0") return "";
    return value;
  };

  const parseTags = (str) => {
    if (!str || typeof str !== "string") return [];
    return str.split(",").map((t) => t.trim()).filter(Boolean);
  };
  const tagsToString = (arr) => (Array.isArray(arr) && arr.length ? arr.join(", ") : "");

  const [requestDetails, setRequestDetails] = useState({
    status: normalizeSelectValue(selectedConversation?.status),
    projectId: normalizeSelectValue(selectedConversation?.projectId),
    channelId: normalizeSelectValue(selectedConversation?.channelId),
    typeId: normalizeSelectValue(selectedConversation?.typeId),
    typeType: normalizeSelectValue(selectedConversation?.typeType),
    subtype1: normalizeSelectValue(selectedConversation?.subtype1),
    subtype2: normalizeSelectValue(selectedConversation?.subtype2),
    subtype3: normalizeSelectValue(selectedConversation?.subtype3),
    userId: normalizeSelectValue(selectedConversation?.userId),
    agentId: normalizeSelectValue(selectedConversation?.agentId),
    potential: normalizeSelectValue(selectedConversation?.potential),
    nf: selectedConversation?.nf ?? "",
    tags: [],
  });
  const [requestTagInput, setRequestTagInput] = useState("");

  /* Re-sincroniza ao trocar a conversa */
  useEffect(() => {
    if (!selectedConversation) return;
    const tagsRaw = selectedConversation.tags ?? "";
    setRequestDetails({
      status: normalizeSelectValue(selectedConversation.status),
      projectId: normalizeSelectValue(selectedConversation.projectId),
      channelId: normalizeSelectValue(selectedConversation.channelId),
      typeId: normalizeSelectValue(selectedConversation.typeId),
      typeType: normalizeSelectValue(selectedConversation.typeType),
      subtype1: normalizeSelectValue(selectedConversation.subtype1),
      subtype2: normalizeSelectValue(selectedConversation.subtype2),
      subtype3: normalizeSelectValue(selectedConversation.subtype3),
      userId: normalizeSelectValue(selectedConversation.userId),
      agentId: normalizeSelectValue(selectedConversation.agentId),
      potential: normalizeSelectValue(selectedConversation.potential),
      nextContactAt: nextContactToInputValue(selectedConversation.nextContactAt),
      nf: selectedConversation.nf ?? "",
      tags: parseTags(tagsRaw),
    });
  }, [selectedConversation]);

  const [loadingFields, setLoadingFields] = useState({});
  const { data: projectsData } = useCompanies();
  const { data: channelsData } = useChannels();
  const { data: typesData } = useTypes();
  // Permite atribuir/transferir também para ChatBots (ura=1).
  const { data: usersData } = useUsers(1, 200, "", "todos");
  const { data: agentsData } = useAgents(1, 200, "");
  const { updateMutation } = useRequests();
  const agentsList = agentsData?.agents ?? [];
  const usersList = usersData?.users ?? [];

  const [subtypes, setSubtypes] = useState({
    subtype1: [],
    subtype2: [],
    subtype3: [],
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* =================== Helpers de defaults de subtipos =================== */
  const findDefaultChildId = (parentId) => {
    if (!parentId || !typesData?.types) return null;
    const all = typesData.types.filter((t) => t.id_pai === parentId);
    if (!all.length) return null;
    const active = all.filter((t) => t.status == null || Number(t.status) === 1);
    const base = active.length ? active : all;
    const withDefault = base.filter((t) => Number(t.default) === 1);
    const chosen = withDefault[withDefault.length - 1] || base[0];
    return chosen?.id ?? null;
  };

  const resolveDefaultChainForType = (typeId) => {
    const s1 = findDefaultChildId(typeId);
    const s2 = s1 != null ? findDefaultChildId(s1) : null;
    const s3 = s2 != null ? findDefaultChildId(s2) : null;
    return { s1, s2, s3 };
  };

  /* =================== Cascata de subtipos =================== */
  // 1) quando muda o typeId -> recarrega lista de opções de Subtipo 1
  useEffect(() => {
    if (requestDetails.typeId) {
      const s1 =
        typesData?.types?.filter((t) => t.id_pai === requestDetails.typeId) ||
        [];
      setSubtypes((prev) => ({ ...prev, subtype1: s1 }));
    } else {
      setSubtypes((prev) => ({ ...prev, subtype1: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestDetails.typeId, typesData]);

  // 2) quando muda o Subtipo 1 -> recarrega lista de opções de Subtipo 2
  useEffect(() => {
    if (requestDetails.subtype1) {
      const s2 =
        typesData?.types?.filter(
          (t) => t.id_pai === requestDetails.subtype1
        ) || [];
      setSubtypes((prev) => ({ ...prev, subtype2: s2 }));
    } else {
      setSubtypes((prev) => ({ ...prev, subtype2: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestDetails.subtype1, typesData]);

  // 3) quando muda o Subtipo 2 -> recarrega lista de opções de Subtipo 3
  useEffect(() => {
    if (requestDetails.subtype2) {
      const s3 =
        typesData?.types?.filter(
          (t) => t.id_pai === requestDetails.subtype2
        ) || [];
      setSubtypes((prev) => ({ ...prev, subtype3: s3 }));
    } else {
      setSubtypes((prev) => ({ ...prev, subtype3: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestDetails.subtype2, typesData]);

  /* =================== Atualização no backend =================== */
  const apiFieldMap = {
    status: "status",
    projectId: "id_projeto",
    channelId: "id_origem",
    typeId: "id_tipo",
    subtype1: "id_subtipo1",
    subtype2: "id_subtipo2",
    subtype3: "id_subtipo3",
    userId: "id_usuario",
    agentId: "id_agente",
    potential: "potencial",
    nextContactAt: "dataproximocontato",
    nf: "nf",
    tags: "tags",
  };

  const handleRequestTagAdd = () => {
    const t = requestTagInput.trim();
    if (!t) return;
    const next = [...requestDetails.tags];
    if (next.includes(t)) {
      setRequestTagInput("");
      return;
    }
    next.push(t);
    handleFieldChange("tags", tagsToString(next));
    setRequestTagInput("");
  };

  const handleRequestTagRemove = (index) => {
    const next = requestDetails.tags.filter((_, i) => i !== index);
    handleFieldChange("tags", tagsToString(next));
  };

  const handleFieldChange = (field, value) => {
    console.log("handleFieldChange", field, value);
    if (requestDetails[field] === value) return;

    const updates = { [field]: value };
    const payload = { id: selectedConversation.id };
    let successPayload = { [field]: value };

    if (field === "agentId") {
      payload.id_agente = value || null;
      payload.id_usuario = value ? null : requestDetails.userId;
      updates.userId = value ? "" : requestDetails.userId;
      const agent = agentsList.find((a) => String(a.id) === String(value));
      successPayload = {
        ...updates,
        id_agente: payload.id_agente,
        id_usuario: payload.id_usuario,
        agentName: agent ? (agent.name ?? agent.nome) : null,
      };
    } else if (field === "userId") {
      payload.id_usuario = value || null;
      payload.id_agente = value ? null : requestDetails.agentId;
      updates.agentId = value ? "" : requestDetails.agentId;
      successPayload = {
        ...updates,
        id_usuario: payload.id_usuario,
        id_agente: payload.id_agente,
        agentName: null,
      };
    } else if (field === "typeId") {
      // Ao mudar o tipo, preenche automaticamente Subtipo 1/2/3 com os defaults (se existirem)
      const { s1, s2, s3 } = resolveDefaultChainForType(value);

      updates.subtype1 = s1 ?? "";
      updates.subtype2 = s2 ?? "";
      updates.subtype3 = s3 ?? "";

      payload.id_tipo = value;
      payload.id_subtipo1 = s1 ?? null;
      payload.id_subtipo2 = s2 ?? null;
      payload.id_subtipo3 = s3 ?? null;

      successPayload = {
        ...successPayload,
        id_tipo: value,
        id_subtipo1: s1 ?? null,
        id_subtipo2: s2 ?? null,
        id_subtipo3: s3 ?? null,
      };
    } else if (field === "subtype1") {
      // Ao mudar o Subtipo 1, define automaticamente Subtipo 2/3 defaults (se existirem)
      const s2 = findDefaultChildId(value);
      const s3 = s2 != null ? findDefaultChildId(s2) : null;

      updates.subtype2 = s2 ?? "";
      updates.subtype3 = s3 ?? "";

      payload.id_subtipo1 = value;
      payload.id_subtipo2 = s2 ?? null;
      payload.id_subtipo3 = s3 ?? null;

      successPayload = {
        ...successPayload,
        id_subtipo1: value,
        id_subtipo2: s2 ?? null,
        id_subtipo3: s3 ?? null,
      };
    } else if (field === "subtype2") {
      // Ao mudar o Subtipo 2, define automaticamente Subtipo 3 default (se existir)
      const s3 = findDefaultChildId(value);

      updates.subtype3 = s3 ?? "";

      payload.id_subtipo2 = value;
      payload.id_subtipo3 = s3 ?? null;

      successPayload = {
        ...successPayload,
        id_subtipo2: value,
        id_subtipo3: s3 ?? null,
      };
    } else if (field === "tags") {
      // tags: value é string para API; estado local mantém array em requestDetails.tags
      const tagArray = typeof value === "string" ? parseTags(value) : (Array.isArray(value) ? value : []);
      updates.tags = tagArray;
      payload.tags = typeof value === "string" ? value : tagsToString(value);
      successPayload.tags = payload.tags;
    } else if (field === "status") {
      payload.status = value;
      successPayload.status = value;
      if (Number(value) !== 3) {
        updates.nextContactAt = "";
        successPayload.nextContactAt = null;
        successPayload.dataproximocontato = null;
      }
    } else if (field === "nextContactAt") {
      const raw = value && String(value).trim() ? String(value).trim() : null;
      payload.dataproximocontato = raw;
      successPayload.dataproximocontato = raw;
      successPayload.nextContactAt = raw;
    } else {
      const apiField = apiFieldMap[field] ?? field;
      payload[apiField] = value;
      successPayload[apiField] = value;
    }

    setRequestDetails((prev) => ({ ...prev, ...updates }));
    const loadingKeys = field === "agentId" || field === "userId" ? { userId: true, agentId: true } : { [field]: true };
    setLoadingFields((prev) => ({ ...prev, ...loadingKeys }));

    updateMutation.mutate(
      payload,
      {
        onSuccess: () => {
          setSnackbar({
            open: true,
            message: "Atualização bem-sucedida!",
            severity: "success",
          });
          const doneKeys = field === "agentId" || field === "userId" ? { userId: false, agentId: false } : { [field]: false };
          setLoadingFields((prev) => ({ ...prev, ...doneKeys }));
          onSelectedConversationUpdate?.(successPayload);
          if (typeof onRefetchConversationDetails === "function") {
            onRefetchConversationDetails();
          }
        },
        onError: () => {
          setSnackbar({
            open: true,
            message: "Erro ao atualizar o campo.",
            severity: "error",
          });
          const doneKeys = field === "agentId" || field === "userId" ? { userId: false, agentId: false } : { [field]: false };
          setLoadingFields((prev) => ({ ...prev, ...doneKeys }));
        },
      }
    );
  };

  const handleAssigneeChange = (next) => {
    console.log('handleAssigneeChange', next, selectedConversation);
    if (!selectedConversation?.id || !next) return;

    const payload = { id: selectedConversation.id };
    const updates = {};
    const successPayload = {};

    if (next.type === "queue") {
      payload.id_usuario = null;
      payload.id_agente = null;

      updates.userId = null;
      updates.agentId = null;

      successPayload.id_usuario = null;
      successPayload.id_agente = null;
      successPayload.userId = null;
      successPayload.agentId = null;
      successPayload.assignedTo = null;
      successPayload.userName = null;
      successPayload.agentName = null;
    } else if (next.type === "agent") {
      let agentId = null;

      if (next.raw) {
        const a = next.raw;
        agentId =
          a.id ??
          a.id_agente ??
          a.agentId ??
          null;
      }

      if (!agentId && next.id) {
        agentId = next.id;
      }

      if (!agentId && next.label) {
        const found = agentsList.find((a) => {
          const name = a.name ?? a.nome ?? "";
          return name === next.label;
        });
        if (found) {
          agentId =
            found.id ??
            found.id_agente ??
            found.agentId ??
            null;
        }
      }

      payload.id_agente = agentId || null;
      payload.id_usuario = null;

      updates.agentId = payload.id_agente;
      updates.userId = null;

      successPayload.id_agente = payload.id_agente;
      successPayload.id_usuario = null;
      successPayload.agentId = payload.id_agente;
      successPayload.userId = null;
      successPayload.agentName = next.label;
      successPayload.assignedTo = null;
      successPayload.userName = null;
    } else if (next.type === "user") {
      payload.id_usuario = next.id ? Number(next.id) : null;
      payload.id_agente = null;

      updates.userId = payload.id_usuario;
      updates.agentId = null;

      successPayload.id_usuario = payload.id_usuario;
      successPayload.id_agente = null;
      successPayload.userId = payload.id_usuario;
      successPayload.agentId = null;
      successPayload.assignedTo = next.label;
      successPayload.userName = next.label;
      successPayload.agentName = null;
    }

    setRequestDetails((prev) => ({
      ...prev,
      userId: updates.userId ?? null,
      agentId: updates.agentId ?? null,
    }));

    setLoadingFields((prev) => ({
      ...prev,
      userId: true,
      agentId: true,
    }));

    updateMutation.mutate(payload, {
      onSuccess: () => {
        setSnackbar({
          open: true,
          message: "Responsável atualizado com sucesso!",
          severity: "success",
        });
        setLoadingFields((prev) => ({
          ...prev,
          userId: false,
          agentId: false,
        }));
        onSelectedConversationUpdate?.(successPayload);
        if (typeof onRefetchConversationDetails === "function") {
          onRefetchConversationDetails();
        }
      },
      onError: () => {
        setSnackbar({
          open: true,
          message: "Erro ao atualizar o responsável.",
          severity: "error",
        });
        setLoadingFields((prev) => ({
          ...prev,
          userId: false,
          agentId: false,
        }));
      },
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const renderLoadingOrIcon = (field) =>
    loadingFields[field] ? <CircularProgress size={20} /> : null;

  // console.log("RequestDetails renderizado", requestDetails.typeType===0, requestDetails.typeType, requestDetails, selectedConversation);
  /* =================== Render =================== */
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <StatusSelector
            currentStatus={requestDetails.status}
            onStatusChange={(newStatus) =>
              handleFieldChange("status", newStatus)
            }
          />
        </Grid>

        {Number(requestDetails.status) === 3 && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Data e hora do próximo contato"
              type="datetime-local"
              value={requestDetails.nextContactAt || ""}
              onChange={(e) => handleFieldChange("nextContactAt", e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Você receberá um lembrete neste horário (solicitação atribuída a você)."
              disabled={!selectedConversation?.id}
              InputProps={{
                endAdornment: renderLoadingOrIcon("nextContactAt"),
              }}
            />
          </Grid>
        )}

        {/* Tags da solicitação */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
            {(requestDetails.tags || []).map((tag, index) => (
              <Chip
                key={`${tag}-${index}`}
                label={tag}
                size="small"
                onDelete={() => handleRequestTagRemove(index)}
                sx={{ mb: 0.5 }}
              />
            ))}
            <TextField
              size="small"
              placeholder="Tag da solicitação (Enter)"
              value={requestTagInput}
              onChange={(e) => setRequestTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  handleRequestTagAdd();
                }
              }}
              sx={{ minWidth: 160, flex: 1 }}
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={handleRequestTagAdd} aria-label="Adicionar tag">
                    <AddIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Box>
        </Grid>

        {/* Empresa */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Empresa</InputLabel>
            <Select
              value={
                projectsData?.companies?.length
                  ? normalizeSelectValue(requestDetails.projectId)
                  : ""
              }
              onChange={(e) =>
                handleFieldChange("projectId", e.target.value)
              }
              endAdornment={renderLoadingOrIcon("projectId")}
            >
              {projectsData?.companies?.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Canal */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Canal</InputLabel>
            <Select
              value={
                channelsData?.channels?.length
                  ? normalizeSelectValue(requestDetails.channelId)
                  : ""
              }
              onChange={(e) =>
                handleFieldChange("channelId", e.target.value)
              }
              endAdornment={renderLoadingOrIcon("channelId")}
            >
              {channelsData?.channels?.map((channel) => (
                <MenuItem key={channel.id} value={channel.id}>
                  {channel.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Tipo */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={
                typesData?.types?.filter((t) => !t.id_pai)?.length
                  ? normalizeSelectValue(requestDetails.typeId)
                  : ""
              }
              onChange={(e) =>
                handleFieldChange("typeId", e.target.value)
              }
              endAdornment={renderLoadingOrIcon("typeId")}
            >
              {typesData?.types
                ?.filter((t) => !t.id_pai)
                .map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.nome}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Subtipo 1 */}
        {subtypes.subtype1?.length > 0 && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Subtipo 1</InputLabel>
              <Select
                value={requestDetails.subtype1}
                onChange={(e) =>
                  handleFieldChange("subtype1", e.target.value)
                }
                endAdornment={renderLoadingOrIcon("subtype1")}
              >
                {subtypes.subtype1.map((st) => (
                  <MenuItem key={st.id} value={st.id}>
                    {st.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Subtipo 2 */}
        {subtypes.subtype2?.length > 0 && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Subtipo 2</InputLabel>
              <Select
                value={requestDetails.subtype2}
                onChange={(e) =>
                  handleFieldChange("subtype2", e.target.value)
                }
                endAdornment={renderLoadingOrIcon("subtype2")}
              >
                {subtypes.subtype2.map((st) => (
                  <MenuItem key={st.id} value={st.id}>
                    {st.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Subtipo 3 */}
        {subtypes.subtype3?.length > 0 && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Subtipo 3</InputLabel>
              <Select
                value={requestDetails.subtype3}
                onChange={(e) =>
                  handleFieldChange("subtype3", e.target.value)
                }
                endAdornment={renderLoadingOrIcon("subtype3")}
              >
                {subtypes.subtype3.map((st) => (
                  <MenuItem key={st.id} value={st.id}>
                    {st.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Responsável: fila / usuário / agente de IA */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Responsável</InputLabel>
            <Select
              value={(() => {
                if (requestDetails.agentId) {
                  return `agent:${requestDetails.agentId}`;
                }
                if (requestDetails.userId) {
                  return `user:${requestDetails.userId}`;
                }
                return "queue";
              })()}
              onChange={(e) => {
                const value = e.target.value;

                if (value === "queue") {
                  handleAssigneeChange({ type: "queue" });
                  return;
                }

                if (typeof value === "string" && value.startsWith("agent:")) {
                  const id = value.split(":")[1];
                  const agent =
                    agentsList.find(
                      (a) =>
                        String(
                          a.id ?? a.id_agente ?? a.agentId ?? ""
                        ) === String(id)
                    ) || null;

                  const label =
                    agent?.name ??
                    agent?.nome ??
                    selectedConversation?.agentName ??
                    "Agente de IA";

                  handleAssigneeChange({
                    type: "agent",
                    id,
                    label,
                    raw: agent || undefined,
                  });
                  return;
                }

                if (typeof value === "string" && value.startsWith("user:")) {
                  const id = value.split(":")[1];
                  const user =
                    usersList.find(
                      (u) => String(u.id ?? "") === String(id)
                    ) || null;

                  const label =
                    user?.nome ??
                    user?.name ??
                    selectedConversation?.userName ??
                    selectedConversation?.assignedTo ??
                    "Usuário";

                  handleAssigneeChange({
                    type: "user",
                    id,
                    label,
                  });
                }
              }}
              endAdornment={
                loadingFields.userId || loadingFields.agentId
                  ? renderLoadingOrIcon("userId")
                  : null
              }
            >
              <MenuItem value="queue">Fila (sem atribuição)</MenuItem>

              {agentsList.length > 0 && (
                <MenuItem disabled sx={{ fontWeight: "bold", opacity: 0.8 }}>
                  Agentes de IA
                </MenuItem>
              )}

              {agentsList
                .map((a) => {
                  const id = a.id ?? a.id_agente ?? a.agentId;
                  const label =
                    a.name ?? a.nome ?? `Agente de IA #${id ?? "sem-id"}`;
                  return (
                    <MenuItem
                      key={`agent-${id ?? "sem-id"}`}
                      value={`agent:${id}`}
                      sx={{ fontWeight: "bold" }}
                    >
                      {label}
                    </MenuItem>
                  );
                })}

              {usersList.length > 0 && (
                <MenuItem disabled sx={{ fontWeight: "bold", opacity: 0.8 }}>
                  Usuários
                </MenuItem>
              )}

              {usersList.map((user) => (
                <MenuItem
                  key={`user-${user.id}`}
                  value={`user:${user.id}`}
                >
                  {user.nome ?? user.name ?? `Usuário #${user.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Potencial */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Potencial</InputLabel>
            <Select
              value={normalizeSelectValue(requestDetails.potential)}
              onChange={(e) =>
                handleFieldChange("potential", e.target.value)
              }
              endAdornment={renderLoadingOrIcon("potential")}
            >
              <MenuItem value={1}>Baixo</MenuItem>
              <MenuItem value={2}>Médio</MenuItem>
              <MenuItem value={3}>Alto</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {requestDetails.typeType===0 && <Grid item xs={12}>
          <TextField
            label="NF"
            fullWidth
            value={requestDetails.nf}
            onChange={(e) => handleFieldChange("nf", e.target.value)}
          />
        </Grid>}
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};

export default RequestDetails;
